import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import AppLayout from "../../components/AppLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useTerms } from "../../lib/BrandContext";
import { useSmartBack } from "../../lib/backNavigation";
import { Checkbox } from "../../components/ui/checkbox";
import api, { formatApiErrorDetail } from "../../lib/api";
import { showPlanLimitToast, isPlanLimitError } from "../../lib/upgradeCta";
import { toast } from "sonner";

const CHARGE_TYPES = [
  { value: "monthly_dues", label: "Monthly dues" },
  { value: "uniform", label: "Uniform" },
  { value: "tournament", label: "Tournament" },
  { value: "fundraiser", label: "Fundraiser" },
  { value: "training", label: "Training session" },
  { value: "custom", label: "Custom" },
];

const METHODS = [
  { value: "venmo", label: "Venmo" },
  { value: "cashapp", label: "Cash App" },
  { value: "zelle", label: "Zelle" },
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "other", label: "Other" },
];

export default function CreateCharge() {
  const { terms } = useTerms();
  const navigate = useNavigate();
  const back = useSmartBack("/coach/charges", "fees");
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    title: "",
    description: "",
    type: "monthly_dues",
    amount: "",
    due_date: "",
    team_id: "",
    payment_instructions: "",
    accepted_methods: ["venmo", "zelle", "cashapp", "cash", "check"],
    recurring_monthly: false,
    status: "active",
  });
  // Assignment scope. `assignScope === "team"` → send player_ids=null and
  // let the backend fan out to the whole roster (existing behavior).
  // `assignScope === "selected"` → send an explicit list. Default is
  // "team" so the fast MVP workflow stays intact for coaches who charge
  // the whole roster every time.
  const [assignScope, setAssignScope] = useState("team");
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [teamRoster, setTeamRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);

  useEffect(() => {
    api.get("/teams")
      .then((r) => { setTeams(r.data); if (r.data[0]) setForm((p) => ({ ...p, team_id: r.data[0].id })); })
      .catch(() => setTeams([]));
  }, []);

  // Load the roster for the currently-selected team whenever the coach
  // switches teams. Reset per-team player picks so we never accidentally
  // send a stale player_id from a different team.
  useEffect(() => {
    if (!form.team_id) { setTeamRoster([]); setSelectedPlayerIds([]); return; }
    setRosterLoading(true);
    api.get(`/players?team_id=${form.team_id}`)
      .then((r) => setTeamRoster((r.data || []).filter((p) => !p.archived)))
      .catch(() => setTeamRoster([]))
      .finally(() => setRosterLoading(false));
    setSelectedPlayerIds([]);
  }, [form.team_id]);

  const toggleMethod = (m) => {
    setForm((p) => ({
      ...p,
      accepted_methods: p.accepted_methods.includes(m) ? p.accepted_methods.filter((x) => x !== m) : [...p.accepted_methods, m],
    }));
  };

  const togglePlayer = (id) => {
    setSelectedPlayerIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      // If the coach chose "Selected players", require at least one and
      // send that list. Otherwise send player_ids=null so the backend
      // fans out to the entire team (unchanged behavior).
      if (assignScope === "selected" && selectedPlayerIds.length === 0) {
        setError(`Pick at least one ${terms.athlete} to assign this fee to.`);
        setLoading(false);
        return;
      }
      const body = {
        ...form,
        amount: parseFloat(form.amount),
        player_ids: assignScope === "selected" ? selectedPlayerIds : null,
      };
      await api.post("/charges", body);
      toast.success("Fee created successfully", {
        description: "You're all set — track payments under the Payments tab.",
      });
      navigate(back.to);
    } catch (e) {
      if (isPlanLimitError(e)) {
        showPlanLimitToast(e, navigate, { fallback: "Could not create fee" });
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail));
      }
    } finally { setLoading(false); }
  };

  return (
    <AppLayout>
      <Link to={back.to} className="inline-flex items-center gap-1 text-sm font-semibold text-ink-500 hover:text-ink-900" data-testid="create-charge-back">
        <ArrowLeft className="h-4 w-4" /> Back to {back.label}
      </Link>
      <div className="mt-3">
        <h1 className="font-heading text-3xl font-bold text-ink-900 sm:text-4xl">Add a Fee</h1>
        <p className="mt-1 text-base text-ink-600">Dues, uniforms, tournaments — anything {terms.athletes} owe.</p>
      </div>

      {teams.length === 0 ? (
        <div className="mt-6 max-w-xl rounded-xl border-2 border-dashed border-ink-200 bg-white p-10 text-center" data-testid="create-charge-no-team">
          <p className="font-heading text-lg font-bold text-ink-900">Create a {terms.team} first</p>
          <p className="mt-2 text-sm text-ink-500">
            Fees are assigned to a {terms.team} (and its {terms.athletes}). Add a {terms.team} before creating your first fee.
          </p>
          <Link to="/coach/teams" className="mt-4 inline-block">
            <Button className="h-11 bg-brand px-5 font-semibold text-white hover:bg-brand-hover" data-testid="create-charge-go-teams">
              Go to {terms.teams}
            </Button>
          </Link>
        </div>
      ) : (
      <form onSubmit={onSubmit} className="mt-6 max-w-3xl space-y-6" data-testid="create-charge-form">
        <div className="rounded-xl border border-ink-100 bg-white p-6 shadow-sm">
          <h3 className="h3">Fee Details</h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Title</Label><Input required placeholder="March Monthly Dues" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} className="h-12" data-testid="charge-title-input" /></div>
            <div><Label>Type</Label>
              <select className="mt-1 h-12 w-full rounded-lg border border-ink-200 bg-white px-3" value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))} data-testid="charge-type-select">
                {CHARGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div><Label>Amount</Label><Input type="number" step="0.01" min="0" required placeholder="95" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} className="h-12" data-testid="charge-amount-input" /></div>
            <div><Label>Due date</Label><Input type="date" required value={form.due_date} onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))} className="h-12" data-testid="charge-due-input" /></div>
            <div><Label>{terms.Team}</Label>
              <select required className="mt-1 h-12 w-full rounded-lg border border-ink-200 bg-white px-3" value={form.team_id} onChange={(e) => setForm((p) => ({ ...p, team_id: e.target.value }))} data-testid="charge-team-select">
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2"><Label>Description (optional)</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} data-testid="charge-description-input" /></div>
            <div className="sm:col-span-2">
              <p className="text-sm font-semibold text-ink-900">Assign fee to</p>
              <p className="mt-0.5 text-xs text-ink-500">
                Charge the whole {terms.team}, or pick specific {terms.athletes}.
              </p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Assign fee to">
                {[
                  { value: "team", label: `Entire ${terms.team}`, hint: `Every active ${terms.athlete} on this ${terms.team} gets this fee.`, testid: "charge-assign-team" },
                  { value: "selected", label: `Selected ${terms.athletes}`, hint: `Pick one or more ${terms.athletes} — only they'll get this fee.`, testid: "charge-assign-selected" },
                ].map((opt) => {
                  const selected = assignScope === opt.value;
                  return (
                    <label
                      key={opt.value}
                      role="radio"
                      aria-checked={selected}
                      tabIndex={0}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${selected ? "border-brand bg-brand-50 ring-1 ring-brand" : "border-ink-200 bg-white hover:border-ink-300"}`}
                      data-testid={opt.testid}
                      onKeyDown={(e) => {
                        if (e.key === " " || e.key === "Enter") { e.preventDefault(); setAssignScope(opt.value); }
                      }}
                    >
                      <input
                        type="radio"
                        name="charge-assign"
                        className="mt-0.5 h-4 w-4 shrink-0 accent-brand"
                        checked={selected}
                        onChange={() => setAssignScope(opt.value)}
                      />
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-ink-900">{opt.label}</p>
                        <p className="text-xs text-ink-500">{opt.hint}</p>
                      </div>
                    </label>
                  );
                })}
              </div>

              {assignScope === "selected" ? (
                <div className="mt-3 rounded-lg border border-ink-200 bg-white p-4" data-testid="charge-assign-roster">
                  <div className="flex items-baseline justify-between">
                    <p className="text-sm font-semibold text-ink-900">
                      Pick {terms.athletes}
                    </p>
                    <p className="text-xs font-semibold text-ink-500" data-testid="charge-assign-selected-count">
                      {selectedPlayerIds.length} selected
                    </p>
                  </div>
                  {rosterLoading ? (
                    <div className="mt-3 flex items-center gap-2 text-sm text-ink-500">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading roster…
                    </div>
                  ) : teamRoster.length === 0 ? (
                    <p className="mt-3 text-sm text-ink-500">
                      No active {terms.athletes} on this {terms.team} yet. Add {terms.athletes} first, or pick Entire {terms.team}.
                    </p>
                  ) : (
                    <>
                      <div className="mt-2 flex items-center gap-3 text-xs">
                        <button type="button" onClick={() => setSelectedPlayerIds(teamRoster.map((p) => p.id))} className="font-semibold text-brand hover:underline" data-testid="charge-assign-select-all">Select all</button>
                        <span className="text-ink-300">·</span>
                        <button type="button" onClick={() => setSelectedPlayerIds([])} className="font-semibold text-ink-600 hover:underline" data-testid="charge-assign-clear">Clear</button>
                      </div>
                      <ul className="mt-2 max-h-56 divide-y divide-ink-100 overflow-y-auto">
                        {teamRoster.map((p) => {
                          const on = selectedPlayerIds.includes(p.id);
                          return (
                            <li key={p.id}>
                              <label
                                className="flex cursor-pointer items-center gap-3 py-2"
                                data-testid={`charge-assign-player-${p.id}`}
                              >
                                <Checkbox checked={on} onCheckedChange={() => togglePlayer(p.id)} />
                                <span className="text-sm font-medium text-ink-900">
                                  {p.first_name} {p.last_name}
                                  {p.jersey ? <span className="ml-1 text-ink-500">#{p.jersey}</span> : null}
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}
                </div>
              ) : null}
            </div>
            <div className="sm:col-span-2">
              <p className="text-sm font-semibold text-ink-900">Fee frequency</p>
              <p className="mt-0.5 text-xs text-ink-500">
                Does this fee happen once, or repeat every month?
              </p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Fee frequency">
                {[
                  {
                    value: false,
                    label: "One-time",
                    hint: "Charge parents once. Most fees are one-time (uniforms, tournaments, jerseys).",
                    testid: "charge-frequency-onetime",
                  },
                  {
                    value: true,
                    label: "Monthly recurring",
                    hint: "Automatically generate this fee on the same day every month until you stop it.",
                    testid: "charge-frequency-monthly",
                  },
                ].map((opt) => {
                  const selected = form.recurring_monthly === opt.value;
                  return (
                    <label
                      key={String(opt.value)}
                      role="radio"
                      aria-checked={selected}
                      tabIndex={0}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${selected ? "border-brand bg-brand-50 ring-1 ring-brand" : "border-ink-200 bg-white hover:border-ink-300"}`}
                      data-testid={opt.testid}
                      onKeyDown={(e) => {
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          setForm((p) => ({ ...p, recurring_monthly: opt.value }));
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="charge-frequency"
                        className="mt-0.5 h-4 w-4 shrink-0 accent-brand"
                        checked={selected}
                        onChange={() => setForm((p) => ({ ...p, recurring_monthly: opt.value }))}
                      />
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-ink-900">{opt.label}</p>
                        <p className="text-xs text-ink-500">{opt.hint}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-ink-100 bg-white p-6 shadow-sm">
          <h3 className="h3">How parents pay</h3>
          <div className="mt-4">
            <Label>Payment instructions</Label>
            <Textarea rows={3} placeholder="e.g. Venmo @CoachMaria. Include your child's name in the note." value={form.payment_instructions} onChange={(e) => setForm((p) => ({ ...p, payment_instructions: e.target.value }))} data-testid="charge-instructions-input" />
          </div>
          <div className="mt-4">
            <Label>Accepted methods</Label>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {METHODS.map((m) => (
                <label key={m.value} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 ${form.accepted_methods.includes(m.value) ? "border-brand bg-brand-50" : "border-ink-200 bg-white"}`} data-testid={`method-${m.value}`}>
                  <Checkbox checked={form.accepted_methods.includes(m.value)} onCheckedChange={() => toggleMethod(m.value)} />
                  <span className="text-sm font-semibold text-ink-900">{m.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p> : null}

        <div className="flex items-center justify-end gap-3">
          <Link to={back.to}><Button type="button" variant="outline" className="h-12 border-ink-300 font-semibold" data-testid="create-charge-cancel">Cancel</Button></Link>
          <Button type="submit" disabled={loading} className="h-12 bg-brand font-semibold text-white hover:bg-brand-hover" data-testid="charge-create-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Fee"}
          </Button>
        </div>
      </form>
      )}
    </AppLayout>
  );
}
